import React, { useEffect } from "react";
import { Router, Route, Switch, Redirect, useLocation } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { useIntl } from "react-intl";
import { createBrowserHistory } from "history";

const history = createBrowserHistory();

import PrivateRoute from "./PrivateRoute";
import MadsRouter from "./MadsRouter";
import useViewer from "../hooks/useViewer";

import ViewerProfileScreen from "../screens/ViewerProfileScreen";
import LoginScreen from "../screens/LoginScreen";
import ForgottenPasswordScreen from "../screens/ForgottenPasswordScreen";
import ConnectionLinkScreen from "../screens/ConnectionLinkScreen";
import ErrorScreen from "../screens/ErrorScreen";

import useTranslations from "../hooks/useTranslations";

import LocalizedPages from "../components/utils/LocalizedPages";

import TemplateListScreen from "../screens/TemplateListScreen";
import EditTemplateScreen from "../screens/EditTemplateScreen";
import NewTemplateScreen from "../screens/NewTemplateScreen";

import NotificationContainer from "../components/notifications/NotificationContainer";
import CampaignsRouter from "./CampaignsRouter";
import UsersRouter from "./UsersRouter";
import ContractRouter from "./ContractRouter";
import WombatPlaygroundScreen from "../components/wombat-ui/WombatPlaygroundScreen";
import ActivityListScreen from "../screens/ActivityListScreen";
import CompanyConfigurationScreen from "../screens/CompanyConfigurationScreen";

import ReportScreenLoader from "../screens/ReportScreenLoader";
import StatisticsRouter from "./AnalyticsRouter";
import GroupListScreen from "../screens/GroupListScreen";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

function AppRouter() {
  const viewer = useViewer();
  const t = useTranslations();
  const { locale } = useIntl();

  return (
    <Router history={history}>
      <ScrollToTop />
      <QueryParamProvider ReactRouterRoute={Route}>
        <ScrollToTop />
        <NotificationContainer />
        <Switch>
          <Route
            path="/login"
            exact
            render={(props) =>
              viewer ? (
                <Redirect to={{ pathname: "/" }} />
              ) : (
                <LoginScreen {...props} />
              )
            }
          />
          <Route
            path="/forgotten-password"
            exact
            component={ForgottenPasswordScreen}
          />
          <Route
            path="/connection-link"
            exact
            component={ConnectionLinkScreen}
          />
          <Route
            path="/wombat-playground"
            exact
            component={WombatPlaygroundScreen}
          />
          <PrivateRoute
            path="/terms-of-sale"
            component={() => (
              <LocalizedPages locale={locale} page="TermsOfSale" />
            )}
          />
          <PrivateRoute
            path="/terms-and-conditions"
            component={() => (
              <LocalizedPages locale={locale} page="TermsAndConditions" />
            )}
          />
          <PrivateRoute
            path="/privacy-policy"
            component={() => (
              <LocalizedPages locale={locale} page="PrivacyPolicy" />
            )}
          />
          <PrivateRoute path="/profile" component={ViewerProfileScreen} />

          <PrivateRoute
            path="/dashboard"
            exact
            component={ActivityListScreen}
          />
          <PrivateRoute path="/mads" component={MadsRouter} />
          <PrivateRoute
            exact
            path={`/reports/:reportId`}
            component={ReportScreenLoader}
          />
          <PrivateRoute
            path="/campaigns"
            component={CampaignsRouter}
          />

          <PrivateRoute path="/users" component={UsersRouter} />
          <PrivateRoute path="/contracts" component={ContractRouter} />

          <PrivateRoute path="/statistics" component={StatisticsRouter} />
          <PrivateRoute
            exact
            path={`/company`}
            component={CompanyConfigurationScreen}
          />
          <PrivateRoute
            exact
            path={`/templates/new`}
            component={NewTemplateScreen}
          />
          <PrivateRoute
            exact
            path={`/templates`}
            component={TemplateListScreen}
          />
          <PrivateRoute
            exact
            path={`/templates/:id/edit`}
            component={EditTemplateScreen}
            key="editTempalte"
          />
          <PrivateRoute
            exact
            path={`/templates/:id/copy`}
            component={EditTemplateScreen}
            key="copyTempalte"
          />
          <PrivateRoute
            exact
            path={`/groups/edit`}
            component={GroupListScreen}
            key="groupList"
          />
          <PrivateRoute
            path="/"
            component={() => (
              <Redirect
                to={
                  viewer.isCapableOfReadingActivities ? "/dashboard" : "/mads"
                }
              />
            )}
            exact
          />

          <Route
            path="*"
            render={() => <ErrorScreen label={t("header.errors.notFound")} />}
          />
        </Switch>
      </QueryParamProvider>
    </Router>
  );
}

export default AppRouter;
